'use client';

import { useBunja } from 'bunja/react';
import { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { intervalSocketBunja } from '@flipster/r230522/original/interval/socket';
import { flex } from '@flipster/styled-system/patterns';
import webviewHandler from '@flipster/utils/webview-handler';

import type { ChartTradeCallbacks } from './chart-trade-callbacks';
import { CustomHeader } from './custom-header';
import { WebviewIntervals } from './custom-header/intervals';
import { Plugins } from './custom-header/plugins';
import { HistoryPlugin } from './custom-header/plugins/history';
import { DraggablePositionsPlugin, DraggableTpSlPlugin } from './custom-header/plugins/order-trackers/draggable-orders';
import { LimitOrdersPlugin } from './custom-header/plugins/order-trackers/limit-orders';
import { ENABLED_FEATURE } from './custom-header/plugins/order-trackers/line-helpers/draggable-tpsl-line-helper/enabled-features';
import { SpotLimitOrdersPlugin } from './custom-header/plugins/order-trackers/spot-limit-orders';
import { SpotTriggerOrdersPlugin } from './custom-header/plugins/order-trackers/spot-trigger-orders';
import { TriggerOrdersPlugin } from './custom-header/plugins/order-trackers/trigger-orders';
import { PositionsPlugin } from './custom-header/plugins/positions';
import { TpSlPlugin } from './custom-header/plugins/tp-sl';
import { getResolveSymbolInfoBaseFn } from './misc/data';
import { toWebviewChartSymbol } from './misc/helper';
import { ChartLoadRecorder, createChartLoadSession } from './observability/chart-load-recorder';
import { chartLoadReporter } from './observability/chart-load-reporter';
import { ObservedDatafeed } from './observability/observed-datafeed';
import { widgetOptions } from './options';
import { datafeed } from './options/datafeed';
import { getDisabledFeatures, getEnabledFeatures } from './options/features';
import SetupChartPatternIdentifier from './plugins/setup-chart-pattern-identifier';
import { TradingViewScope, intervalDataBunja, reconnectionLongTermBunja } from './socket/effect';
import TradingView from './trading-view';
import { TvLayout } from './tv-layout';

export interface TvChartWebviewProps extends ChartTradeCallbacks {
  symbol: string;
  osType: 'Android' | 'iOS' | 'unknown';
  version: string;
  isSpot?: boolean;
  isLoggedIn?: boolean;
  patternId?: string;
  featureFlags?: Record<string, boolean>;
}

const TvChartWebview = ({
  symbol,
  osType,
  version,
  isSpot,
  isLoggedIn,
  patternId,
  featureFlags,
  onOrderDragComplete,
  onOrderCancelClick,
  onPositionClose,
}: PropsWithChildren<TvChartWebviewProps>) => {
  const [tv, setTV] = useState<TradingView>();
  const [initialized, setInitialized] = useState(false);
  const isSpotLimitOrderEnabled = featureFlags?.['25q4-spot-limit-order'];
  const isDraggableEnabled = featureFlags?.[ENABLED_FEATURE.DraggableTpSl] ?? false;
  // Spot: force :MID (native mid often has no suffix; sanitize must not map that to LAST).
  const chartSymbol = useMemo(() => toWebviewChartSymbol(symbol, isSpot), [symbol, isSpot]);
  const [chartLoadSession] = useState(() => createChartLoadSession('webview_chart'));
  const [chartLoadRecorder] = useState(
    () => new ChartLoadRecorder({ session: chartLoadSession, reporter: chartLoadReporter }),
  );
  const [observedDatafeed] = useState(() => new ObservedDatafeed(datafeed, chartLoadRecorder));
  const [initialChartSymbol] = useState(chartSymbol);
  const previousChartSymbolRef = useRef(chartSymbol);

  // 앱(webview)은 드래그 scope out: 라인은 비편집으로 두고 ×(청산/삭제)만 브리지로 native에 위임한다.
  // onDragComplete는 타입상 필요하지만 비편집이라 호출되지 않는다(close-only).
  const draggableCallbacks = useMemo(
    () =>
      isDraggableEnabled && onOrderDragComplete && onOrderCancelClick
        ? { onDragComplete: onOrderDragComplete, onCancelClick: onOrderCancelClick }
        : undefined,
    [isDraggableEnabled, onOrderDragComplete, onOrderCancelClick],
  );

  useBunja(intervalDataBunja);
  useBunja(reconnectionLongTermBunja, [TradingViewScope.bind(tv)]);
  const { subscribeSocketIntervalData, unsubscribeSocketIntervalData, requestIntervalSnapshot } =
    useBunja(intervalSocketBunja);

  // TV Chart에게 symbol 정보를 가져오는 방법과 인터벌 데이터 구독/해지하는 방법 주입
  useLayoutEffect(() => {
    datafeed.isWebview = true;
    datafeed.resolveSymbolInfoBase = getResolveSymbolInfoBaseFn('hash');
    datafeed.requestIntervalSnapshot = requestIntervalSnapshot;
    datafeed.subscribeSocketIntervalData = subscribeSocketIntervalData;
    datafeed.unsubscribeSocketIntervalData = unsubscribeSocketIntervalData;
    const offSymbolNotResolved = datafeed.on('symbolNotResolved', () => {
      webviewHandler.postMessage({ action: 'info', params: { message: 'chart_need_to_reload' } });
    });

    return () => {
      offSymbolNotResolved();
    };
  }, [requestIntervalSnapshot, subscribeSocketIntervalData, unsubscribeSocketIntervalData]);

  useEffect(() => {
    chartLoadRecorder.beginAttempt('initial', { symbol: initialChartSymbol }, chartLoadSession.createdAt);
    chartLoadRecorder.markAt('shell_visible', chartLoadSession.createdAt);
    chartLoadRecorder.mark('module_ready');
    return () => chartLoadRecorder.dispose();
  }, [chartLoadRecorder, chartLoadSession.createdAt, initialChartSymbol]);

  useEffect(() => {
    if (tv) return;
    const instance = new TradingView(
      {
        symbol: chartSymbol,
        custom_css_url: './styles.css?v=5',
        enabled_features: getEnabledFeatures('webview-chart', osType, version, featureFlags),
        disabled_features: getDisabledFeatures('webview-chart', osType, version, featureFlags),
        datafeed: observedDatafeed,
        fullscreen: false,
        debug: false,
        ...widgetOptions,
      },
      { webview: true, chartLoadRecorder },
    );
    instance.on('initialize', () => setInitialized(true));
    setTV(instance);
  }, [chartLoadRecorder, version, chartSymbol, observedDatafeed, tv, osType, featureFlags]);

  useEffect(() => {
    if (!tv || previousChartSymbolRef.current === chartSymbol) return;
    previousChartSymbolRef.current = chartSymbol;
    tv.setSymbol(chartSymbol, 'symbol');
  }, [chartSymbol, tv]);

  return (
    <>
      {initialized && <SetupChartPatternIdentifier tv={tv} patternId={patternId} />}
      <TvLayout
        tv={tv}
        chartLoadRecorder={chartLoadRecorder}
        customHeader={
          initialized && (
            <>
              <CustomHeader.HeaderSection>
                <WebviewIntervals />
              </CustomHeader.HeaderSection>
              <CustomHeader.HeaderSection>
                <Plugins>
                  {!isSpot &&
                    isLoggedIn &&
                    (isDraggableEnabled && onPositionClose ? (
                      <DraggablePositionsPlugin onPositionClose={onPositionClose} />
                    ) : (
                      <PositionsPlugin />
                    ))}
                  {!isSpot &&
                    isLoggedIn &&
                    (draggableCallbacks ? (
                      <DraggableTpSlPlugin callbacks={draggableCallbacks} isWebview />
                    ) : (
                      <TpSlPlugin variant='short' />
                    ))}
                  {!isSpot && isLoggedIn && <LimitOrdersPlugin />}
                  {!isSpot && isLoggedIn && <TriggerOrdersPlugin />}
                  {isSpotLimitOrderEnabled && isSpot && isLoggedIn && <SpotLimitOrdersPlugin />}
                  {isSpot && isLoggedIn && <SpotTriggerOrdersPlugin />}
                  {isLoggedIn && <HistoryPlugin />}
                </Plugins>
                {/* app fullchart button overlay */}
                <div className={flex({ w: 30, h: 1 })} />
              </CustomHeader.HeaderSection>
            </>
          )
        }
        fullscreen
      />
    </>
  );
};

export default TvChartWebview;
